import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { DeviceExtended, MatchedDevice } from '@app/modules/smart-devices/data/smart-devices.interface';
import { DialogConfirmDeviceUnmatchComponent } from '@app/modules/smart-devices/dialogs/dialog-confirm-device-unmatch/dialog-confirm-device-unmatch.component';
import { SmartDevicesService } from '@app/modules/smart-devices/service/smart-devices.service';
import { NotificationType, Property } from '@app/shared/interfaces';
import { SmartlocksService } from '@app/shared/services/smartlocks/smartlocks.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { resolveIntegrationInfo } from '@app/shared/utils';

@Component({
  standalone: false,
  selector: 'sbnb-dialog-edit-device',
  templateUrl: './dialog-edit-device.component.html',
  styleUrls: ['./dialog-edit-device.component.scss'],
})
export class DialogEditDeviceComponent implements OnInit {
  deviceImage = '';
  existingProperties: Property['id'][] = []; // The properties that are already assigned to the device at the time of opening, we need this to calculate the delta, which gets sent to the API in two keys to handle the secondary user permissions case where they can only manage properties they have access to, without affecting others
  properties: Property['id'][] = [];

  validating = false;
  saving = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      device: MatchedDevice | DeviceExtended;
      currentNumber?: string;
    },
    public dialogRef: MatDialogRef<DialogEditDeviceComponent>,
    private smartlocksService: SmartlocksService,
    private deviceService: SmartDevicesService,
    private toast: ToastNotificationsService,
    private auth: AuthenticationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.dialogData.device && !this.dialogData.device.code_length) {
      this.dialogData.device.code_length = this.dialogData.device.min_length;
    }

    // Fetch the image url for the integration
    this.deviceImage = resolveIntegrationInfo(this.dialogData.device.integration_key)?.logo;

    // For editing, set the existing properties
    if (this.dialogData.device.properties) {
      this.existingProperties = this.dialogData.device.properties.map((property) => property.id);
      this.properties = [...this.existingProperties];
    }
  }

  setLockProperties(event: { all_properties: boolean; properties: number[] }) {
    this.properties = event.properties;
  }

  newPropertyChecked(event: { property: number }) {
    const payload = {
      property_id: event.property,
      code_length: this.dialogData.device.code_length,
      lock_id: this.dialogData.device.id,
    };

    this.validating = true;

    if (this.dialogData.device.device_type === 'lock') {
      this.smartlocksService.validateCodeLengthAgainstProperty(payload).subscribe(
        (res) => {
          this.validating = false;
          if (res?.errors) {
            this.toast.open(res.message, 'Dismiss', NotificationType.Error);
            this.properties = this.properties.filter((propId) => propId !== event.property);
            return;
          }
        },
        (err) => {
          this.validating = false;
        }
      );
    } else {
      this.validating = false;
    }
  }

  onSaveClicked() {
    const { addedProperties, removedProperties } = this.determinePropertyChanges();

    if (removedProperties.length > 0 && this.dialogData.device.device_type !== 'thermostat') {
      const confirmUnmatchDialogRef = this.dialog.open(DialogConfirmDeviceUnmatchComponent, {
        width: '720px',
        data: {},
      });

      confirmUnmatchDialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.editDevice(addedProperties, removedProperties);
        }
      });
    } else {
      this.editDevice(addedProperties, removedProperties);
    }
  }

  private editDevice(addedProperties, removedProperties) {
    this.saving = true;

    const { device } = this.dialogData;

    this.deviceService
      .editDevice(
        device.id,
        device.integration,
        this.dialogData.device.code_length,
        addedProperties,
        removedProperties,
        device.device_properties?.battery?.threshold,
        device.guest_access,
        device.device_type
      )
      .subscribe(
        (res) => {
          // refresh the user
          this.auth.getUserDetails(true);

          this.saving = false;
          this.dialogRef.close();
        },
        (err) => {
          this.saving = false;
        }
      );
  }

  private determinePropertyChanges() {
    const addedProperties = this.properties.filter((prop) => !this.existingProperties.includes(prop));
    const removedProperties = this.existingProperties.filter((prop) => !this.properties.includes(prop));

    return { addedProperties, removedProperties };
  }

  isBatteryThresholdValid(): boolean {
    if (this.dialogData?.device?.device_type !== 'lock') {
      return true; // Not applicable for non-lock devices
    }

    const threshold = this.dialogData?.device?.device_properties?.battery?.threshold;
    return typeof threshold === 'number' && threshold >= 0 && threshold <= 99;
  }
}
